// Raccourcis vers les outils externes de la savonnière (agenda + éditeur d'étiquettes Canva).
// Source UNIQUE de la logique, réutilisée par l'accueil (barre de raccourcis) et par le menu ☰
// (MenuNavigation). Les URL viennent des réglages (section « Raccourcis » de l'écran Réglages) :
// on ne redéfinit aucune règle métier ici, on centralise l'ouverture + le repli Canva pour éviter
// tout doublon entre les deux points d'accès.

import { useEffect, useState } from 'react'
import { fetchReglages, URL_CANVA_ETIQUETTES_DEFAUT } from '@/lib/reglages'
import { URL_BD_DOCPRO } from '@shared/constants'

// Générateur de documents BD-DistriDoc : outil externe GRATUIT, sans compte et sans stockage.
// URL FIXE (contrairement à l'agenda et à Canva, qui sont des réglages propres à la cliente) :
// aucun réglage à prévoir, aucune donnée échangée, aucune intégration — un simple lien sortant.
export const URL_BD_DISTRIDOC = 'https://bd-distridoc.netlify.app'

// Espace client BD-DocPro : point d'entrée permanent vers l'application SÉPARÉE qui établit les
// dossiers réglementaires (DIP / RSPC). L'URL est celle de l'accueil DocPro (URL_BD_DOCPRO,
// shared/constants.ts) — l'abonnée s'y connecte pour suivre ses dossiers déjà déposés. Lien
// STATIQUE volontaire : aucun appel réseau, aucun couplage runtime SapoPro -> DocPro (le transport
// d'une demande reste manuel via le fichier .json). Distinct du lien de DÉPÔT (LienDepotDocPro),
// qui vise /demander-dip et n'apparaît qu'après le téléchargement d'une demande.

// URL Canva effective : le modèle de la cliente s'il est renseigné, sinon l'outil d'étiquettes
// Canva par défaut. Fonction PURE (testable), même repli que la section Réglages.
export function urlCanvaEffective(urlCanva: string | null | undefined): string {
  const perso = urlCanva?.trim()
  return perso && perso.length > 0 ? perso : URL_CANVA_ETIQUETTES_DEFAUT
}

// Ouvre une URL externe dans un nouvel onglet, protégée (noopener). No-op si l'URL est vide.
function ouvrirExterne(url: string | null | undefined): void {
  const cible = url?.trim()
  if (cible && cible.length > 0) {
    window.open(cible, '_blank', 'noopener')
  }
}

export interface Raccourcis {
  urlAgenda: string | null // null/'' -> le bouton « agenda » ne doit pas s'afficher (rien à ouvrir)
  ouvrirAgenda: () => void
  ouvrirCanva: () => void // repli automatique sur l'outil Canva par défaut si aucun modèle
  ouvrirDistriDoc: () => void // URL fixe, toujours disponible (aucun réglage à renseigner)
  ouvrirEspaceDocPro: () => void // URL fixe (accueil BD-DocPro), lien statique sans couplage
}

// Charge les URL des réglages au montage et expose les actions d'ouverture. `fetchReglages` peut
// renvoyer null (compte sans réglages enregistrés) : dans ce cas urlAgenda reste null et le bouton
// agenda est masqué, le bouton Canva retombe sur le repli.
export function useRaccourcis(): Raccourcis {
  const [urlAgenda, setUrlAgenda] = useState<string | null>(null)
  const [urlCanva, setUrlCanva] = useState<string | null>(null)

  useEffect(() => {
    let actif = true
    fetchReglages()
      .then((r) => {
        if (!actif) return
        setUrlAgenda(r?.urlAgenda ?? null)
        setUrlCanva(r?.urlCanva ?? null)
      })
      .catch(() => {
        // Réglages indisponibles : on reste sur les valeurs par défaut (agenda masqué, Canva repli).
      })
    return () => {
      actif = false
    }
  }, [])

  return {
    urlAgenda,
    ouvrirAgenda: () => ouvrirExterne(urlAgenda),
    ouvrirCanva: () => ouvrirExterne(urlCanvaEffective(urlCanva)),
    ouvrirDistriDoc: () => ouvrirExterne(URL_BD_DISTRIDOC),
    ouvrirEspaceDocPro: () => ouvrirExterne(URL_BD_DOCPRO),
  }
}
